import {
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { Virtuoso } from "react-virtuoso";
import { FormControl, MenuItem } from "@mui/material";
import Select from "../../components/SearchableSelect";
import { getMemberInitials, roles } from "./utils";
import { membersListStyles } from "./styles";
import { tokens } from "../../theme";
import { useMemo, useState } from "react";
import { SearchRounded } from "@mui/icons-material";
import DataGridButton from "../../components/DataGridButton";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";

const MembersList = ({ env, addMember, modifyMemberRole, deleteMember }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [search, setSearch] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const allMembers = useMemo(
    () =>
      env?.indexedMembers
        ?.filter((email) => email in env?.members)
        ?.map((email) => ({ email: email, role: env?.members[email] })) || [],
    [env?.indexedMembers, env?.members]
  );

  const members = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allMembers;
    return allMembers.filter((member) =>
      member.email.toLowerCase().includes(query)
    );
  }, [allMembers, search]);

  const canAddMember = Boolean(email) && Boolean(role);

  const handleAddMember = (event) => {
    if (!canAddMember) return;
    addMember(env?.id, email, role, event);
    clearAddMember();
  };

  const clearAddMember = () => {
    setEmail("");
    setRole("");
  };
  return (
    <Box sx={membersListStyles.root}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={membersListStyles.titleRow}
      >
        <Typography sx={membersListStyles.title(colors)}>Members</Typography>
        <Box sx={membersListStyles.count(colors)}>{allMembers.length}</Box>
        <Box flexGrow={1} />
        <TextField
          size="small"
          hiddenLabel
          placeholder="Search members"
          sx={membersListStyles.searchField(colors)}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Box sx={membersListStyles.card(colors)}>
        {members.length ? (
          <Virtuoso
            style={{ flex: 1, minHeight: 0 }}
            data={members}
            itemContent={(_index, member) => (
              <UserRole
                envId={env?.id}
                {...member}
                isOwner={member.email === env?.createdBy}
                colors={colors}
                modifyMemberRole={modifyMemberRole}
                deleteMember={deleteMember}
              />
            )}
          />
        ) : (
          <Box sx={membersListStyles.emptyState(colors)}>
            {allMembers.length
              ? "No members match your search"
              : "No members yet"}
          </Box>
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={membersListStyles.addRow(colors)}
        >
          <Autocomplete
            noOptionsText="No Assignee available"
            label="Assignee"
            sx={membersListStyles.emailField}
            size="small"
            value={email || ""}
            fullWidth
            options={env?.addableMembers?.map((v) => v.email)}
            onChange={(event, newValue) => {
              event.preventDefault();
              event.stopPropagation();
              setEmail(newValue);
            }}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add member by email"
                hiddenLabel
                size="small"
              />
            )}
          />

          <Select
            sx={membersListStyles.addRoleField(colors)}
            size="small"
            displayEmpty
            label="ROLE"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="" disabled>
              Role
            </MenuItem>
            {Object?.keys(roles)?.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
          <Tooltip
            title={
              canAddMember ? "Add member" : "Select an assignee and a role"
            }
          >
            <span>
              <DataGridButton
                sx={membersListStyles.addButton(colors)}
                disabled={!canAddMember}
                onClick={(event) => handleAddMember(event)}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  Add
                </Typography>
              </DataGridButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

const UserRole = ({
  envId,
  email,
  role,
  isOwner,
  colors,
  modifyMemberRole,
  deleteMember,
}) => {
  const handleMemberRoleChange = (event) => {
    modifyMemberRole(envId, email, event.target.value);
  };
  const handleDeleteMember = (event) => {
    deleteMember(envId, email, event.target.value);
  };

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={membersListStyles.memberRow(colors)}
    >
      <Avatar sx={membersListStyles.avatar(colors)}>
        {getMemberInitials(email)}
      </Avatar>
      <Box sx={membersListStyles.emailCell}>
        <Tooltip title={email}>
          <Typography sx={membersListStyles.emailText(colors)}>
            {email}
          </Typography>
        </Tooltip>
        {isOwner ? (
          <Typography sx={membersListStyles.emailMeta(colors)}>
            Environment creator
          </Typography>
        ) : null}
      </Box>
      <FormControl sx={membersListStyles.roleField(colors)}>
        <Select
          value={role}
          size="small"
          onChange={(event) => handleMemberRoleChange(event)}
        >
          {Object?.keys(roles)?.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title={`Remove ${email}`}>
        <IconButton
          size="small"
          sx={membersListStyles.deleteAction(colors)}
          onClick={(event) => handleDeleteMember(event)}
        >
          <DeleteOutlineRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default MembersList;
